/**
 * Pyrite company logos: buyer page logic without the page itself (W520, A1, PYR-251).
 *
 * Builds the requests the Apps Script copy accepts and reads its answers. W520 A2b: the page
 * calls the copy's web address directly (the bare /exec address), as the live helper does, because
 * Shopify's app proxy cannot reach Apps Script; a page pass signed by Shopify's Liquid says who
 * is signed in. Plain JavaScript with no DOM, so the same file runs on the storefront and in
 * local tests against the copy's real Core.
 * Live version (A7): checkout hosts are checked here too (W519 W1), and a subtotal is shown
 * from the store's own variant prices (W519 W2; L1: live prices come from the product).
 *
 * Rules kept here:
 * - Only the four actions, always with v: 1 and a fresh random n. No prices are sent:
 *   the server prices every line from the store itself.
 * - One logo per checkout. A saved logo brands only the item families it is approved for.
 * - Answers are read as data only. Nothing here logs, stores or shows private values.
 */
var PyriteCompanyLogosCore = (function () {
  'use strict';

  // W520 A2b: only a Google Apps Script web address, exactly; and the page pass's shape.
  var ENDPOINT = /^https:\/\/script\.google\.com\/macros\/s\/[A-Za-z0-9_-]{20,200}\/exec$/;
  var PAGE_PASS = /^v1\.[1-9][0-9]{0,19}\.[1-9][0-9]{8,10}\.[0-9a-f]{64}$/;
  var ACTIONS = ['whoami', 'library', 'preview', 'prepare'];
  var LOGO_KEY = /^[A-Z0-9]{2,6}-[0-9]{3}$/;
  var NUMERIC_ID = /^[1-9][0-9]{0,19}$/;
  var OP_TOKEN = /^[0-9a-f]{64}$/;
  var UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  var FILE_NAME = /\.(png|jpe?g|svg|pdf|dxf|ai|eps)$/i;
  var FAMILIES = ['micros', 'training'];
  var MAX_LINES = 20;
  var B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  var GENERIC = 'Something went wrong. Please refresh the page and try again, or email us and we will help.';

  function hex(bytes) {
    var out = '';
    for (var i = 0; i < bytes.length; i++) out += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
    return out;
  }

  /** random(n) must return n random bytes (crypto.getRandomValues on the page). */
  function nonce(random) {
    return hex(random(16));
  }

  /** A lowercase UUID v4, as the copy requires for a request ID. */
  function requestId(random) {
    var b = Array.prototype.slice.call(random(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var h = hex(b);
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20, 32);
  }

  /** The web address the section read from the store, or '' when it is missing or not Apps Script's. */
  function parseEndpoint(text) {
    var raw;
    try { raw = JSON.parse(text); } catch (e) { return ''; }
    return typeof raw === 'string' && ENDPOINT.test(raw.trim()) ? raw.trim() : '';
  }

  /** The page pass rendered by Liquid, or '' (signed out, or anything malformed). */
  function parsePass(text) {
    var raw;
    try { raw = JSON.parse(text); } catch (e) { return ''; }
    return typeof raw === 'string' && PAGE_PASS.test(raw) ? raw : '';
  }

  // W520 A7c (W518, test store): Apps Script refuses a POST with a path after /exec (HTTP 401),
  // so every action goes to the bare address; the action travels in the body.
  function url(action, endpoint) {
    if (ACTIONS.indexOf(action) < 0) throw new Error('Unknown action.');
    if (typeof endpoint !== 'string' || !ENDPOINT.test(endpoint)) throw new Error('No web address.');
    return endpoint;
  }

  /** A copy of a request body with the page pass added when there is one. */
  function withPass(body, pass) {
    var out = {};
    Object.keys(body).forEach(function (name) { out[name] = body[name]; });
    if (typeof pass === 'string' && PAGE_PASS.test(pass)) out.pass = pass;
    return out;
  }

  function libraryBody(random) {
    return {v: 1, action: 'library', n: nonce(random)};
  }

  function whoamiBody(random) {
    return {v: 1, action: 'whoami', n: nonce(random)};
  }

  function previewBody(random, key) {
    if (!LOGO_KEY.test(key || '')) throw new Error('Bad logo key.');
    return {v: 1, action: 'preview', n: nonce(random), key: key};
  }

  /**
   * The items this page sells, from the section's JSON. Anything malformed is dropped, so a
   * mistyped block can't send a bad line. Returns [{variantId, label, max, family, price}].
   */
  function parseItems(text) {
    var raw;
    try { raw = JSON.parse(text); } catch (e) { return []; }
    if (!Array.isArray(raw)) return [];
    var seen = {};
    var items = [];
    raw.forEach(function (entry) {
      if (!entry || typeof entry !== 'object') return;
      var id = String(entry.variantId === undefined || entry.variantId === null ? '' : entry.variantId).trim();
      var max = Number(entry.max);
      if (!NUMERIC_ID.test(id) || seen[id] || FAMILIES.indexOf(entry.family) < 0 ||
          typeof entry.label !== 'string' || !entry.label.trim() || !Number.isSafeInteger(max) || max < 1 || max > 100) return;
      seen[id] = true;
      var cents = Number(entry.priceCents);
      items.push({variantId: id, label: entry.label.trim().slice(0, 80), max: max, family: entry.family,
        price: typeof entry.price === 'string' ? entry.price.trim().slice(0, 30) : '',
        priceCents: entry.priceCents !== null && entry.priceCents !== undefined && Number.isSafeInteger(cents) && cents >= 0 ? cents : null});
    });
    return items.slice(0, MAX_LINES);
  }

  /** The saved logo the buyer picked, from the library answer, or null. */
  function savedLogo(library, key) {
    if (!library || !Array.isArray(library.logos)) return null;
    for (var i = 0; i < library.logos.length; i++) if (library.logos[i] && library.logos[i].key === key) return library.logos[i];
    return null;
  }

  /** Whether an item family may carry the chosen logo. A new upload may brand any item. */
  function familyAllowed(choice, library, family) {
    if (!choice) return false;
    if (choice.kind === 'upload') return true;
    var logo = savedLogo(library, choice.key);
    return !!(logo && logo.products && logo.products[family] === true);
  }

  /**
   * The lines for prepare: one branded line per item with a quantity, each within 1..max.
   * Returns {lines} or {problem} in plain words.
   */
  function buildLines(items, quantities, choice, library) {
    var lines = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var qty = Number(quantities[item.variantId] || 0);
      if (!qty) continue;
      if (!Number.isSafeInteger(qty) || qty < 0 || qty > item.max) return {problem: 'Please choose between 1 and ' + item.max + ' for ' + item.label + '.'};
      if (!familyAllowed(choice, library, item.family)) return {problem: item.label + ' can\'t carry the logo you chose. Please set it to 0 or choose another logo.'};
      lines.push({variantId: item.variantId, quantity: qty, branded: true});
    }
    if (!lines.length) return {problem: 'Please choose at least one item.'};
    return {lines: lines};
  }

  /** Strict base64 of bytes, the canonical form the copy checks. */
  function base64(bytes) {
    var out = '';
    var i = 0;
    for (; i + 2 < bytes.length; i += 3) {
      var n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
      out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + B64[n & 63];
    }
    if (i < bytes.length) {
      var rest = bytes.length - i;
      var m = (bytes[i] << 16) | (rest > 1 ? bytes[i + 1] << 8 : 0);
      out += B64[(m >> 18) & 63] + B64[(m >> 12) & 63] + (rest > 1 ? B64[(m >> 6) & 63] : '=') + '=';
    }
    return out;
  }

  /** The upload part of prepare, or {problem}. maxBytes is the page's limit (the server checks again). */
  function uploadFile(name, bytes, maxBytes) {
    if (typeof name !== 'string' || !FILE_NAME.test(name)) return {problem: 'Please upload your logo as a PNG, JPG, SVG, PDF, DXF, AI or EPS file.'};
    if (!bytes || !bytes.length) return {problem: 'That file is empty. Please choose your logo file again.'};
    if (bytes.length > maxBytes) return {problem: 'Your file is larger than ' + Math.round(maxBytes / 1048576) + ' MB. Please upload a smaller version.'};
    return {file: {name: name.slice(-200), size: bytes.length, data: base64(bytes)}};
  }

  /** The prepare body, or {problem}. state: {requestId, op, choice, file}. */
  function prepareBody(random, state, items, quantities, library) {
    if (!state || !UUID_V4.test(state.requestId || '') || !OP_TOKEN.test(state.op || '')) return {problem: 'This page needs a refresh. Please reload it and try again.'};
    var logo;
    if (state.choice && state.choice.kind === 'saved' && savedLogo(library, state.choice.key)) {
      logo = {kind: 'saved', key: state.choice.key};
    } else if (state.choice && state.choice.kind === 'upload') {
      if (!state.file) return {problem: 'Please attach your logo file before continuing.'};
      logo = {kind: 'upload', file: state.file};
    } else {
      return {problem: 'Please choose a saved logo or upload your logo.'};
    }
    var built = buildLines(items, quantities, state.choice, library);
    if (built.problem) return built;
    return {body: {v: 1, action: 'prepare', n: nonce(random), requestId: state.requestId, op: state.op, lines: built.lines, logo: logo}};
  }

  /** Reads an answer. Anything that isn't the copy's JSON becomes a generic, safe refusal. */
  function parseAnswer(text) {
    var value;
    try { value = JSON.parse(text); } catch (e) { return {ok: false, code: 'PAGE', message: GENERIC, retry: true}; }
    if (!value || typeof value !== 'object' || typeof value.ok !== 'boolean') return {ok: false, code: 'PAGE', message: GENERIC, retry: true};
    if (!value.ok) {
      return {ok: false, code: typeof value.code === 'string' ? value.code : 'PAGE',
        message: typeof value.message === 'string' && value.message ? value.message : GENERIC, retry: value.retry === true,
        variantIds: Array.isArray(value.variantIds) ? value.variantIds.filter(function (id) { return NUMERIC_ID.test(String(id)); }).map(String) : []};
    }
    return value;
  }

  /** The library answer, checked: logos keep only safe fields; op must be present. */
  function readLibrary(answer) {
    if (!answer || answer.ok !== true || answer.action !== 'library' || !OP_TOKEN.test(answer.op || '')) return null;
    var logos = (Array.isArray(answer.logos) ? answer.logos : []).filter(function (logo) {
      return logo && LOGO_KEY.test(logo.key || '') && typeof logo.label === 'string';
    }).map(function (logo) {
      var products = logo.products || {};
      return {key: logo.key, label: logo.label.slice(0, 80), preview: logo.preview === true,
        products: {micros: products.micros === true, training: products.training === true},
        // W533: personal approval or company logo, from the copy (w520-dev-10 on); null from an older copy.
        source: logo.source === 'personal' || logo.source === 'company' ? logo.source : null};
    });
    var fee = answer.setup && typeof answer.setup.amount === 'string' && /^[0-9]{1,6}(\.[0-9]{2})?$/.test(answer.setup.amount) ? answer.setup.amount : null;
    return {signedIn: answer.signedIn === true, company: answer.company && typeof answer.company.name === 'string' ? answer.company.name.slice(0, 80) : null,
      logos: logos, fee: fee, op: answer.op};
  }

  /**
   * Only an https checkout address on one of the page's own hosts (the store's domains, or
   * checkout.shopify.com) is followed (W519 W1). The server checks its host too.
   */
  function checkoutUrl(answer, hosts) {
    if (!answer || answer.ok !== true || answer.action !== 'prepare' || typeof answer.checkoutUrl !== 'string') return null;
    var match = /^https:\/\/([a-z0-9.-]+)(?::443)?(?:[\/?][^\s"'<>]*)?$/i.exec(answer.checkoutUrl);
    if (!match || !Array.isArray(hosts)) return null;
    return hosts.indexOf(match[1].toLowerCase()) >= 0 ? answer.checkoutUrl : null;
  }

  /** The page's own hosts, from the section's JSON: lowercase names only. */
  function parseHosts(text) {
    var raw;
    try { raw = JSON.parse(text); } catch (e) { return []; }
    if (!Array.isArray(raw)) return [];
    return raw.filter(function (host) { return typeof host === 'string' && /^[a-z0-9.-]{3,253}$/i.test(host); })
      .map(function (host) { return host.toLowerCase(); });
  }

  /**
   * Subtotal before shipping and tax, in cents, or null when any chosen item has no store price
   * (W519 W2). With a new upload it includes the one-time setup fee.
   */
  function subtotalCents(items, quantities, choice, library) {
    var total = 0;
    var any = false;
    for (var i = 0; i < items.length; i++) {
      var qty = Number(quantities[items[i].variantId] || 0);
      if (!qty) continue;
      if (!Number.isSafeInteger(qty) || qty < 0 || qty > items[i].max || items[i].priceCents === null) return null;
      total += items[i].priceCents * qty;
      any = true;
    }
    if (!any) return null;
    if (choice && choice.kind === 'upload') {
      if (!library || library.fee === null) return null;
      total += Math.round(Number(library.fee) * 100);
    }
    return total;
  }

  function moneyCents(cents) {
    if (cents === null) return '';
    var parts = (cents / 100).toFixed(2).split('.');
    return '$' + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
  }

  /** A preview image only as an inline PNG or JPEG. */
  function previewSource(answer) {
    if (!answer || answer.ok !== true || answer.action !== 'preview' || typeof answer.previewData !== 'string') return null;
    return /^data:image\/(png|jpeg);base64,[A-Za-z0-9+\/]+=*$/.test(answer.previewData) ? answer.previewData : null;
  }

  function money(amount) {
    return amount === null ? '' : '$' + Number(amount).toFixed(2);
  }

  /** What the buyer pays for setup, in words, before checkout. */
  function setupLine(choice, library) {
    if (!choice) return '';
    if (choice.kind === 'saved') return 'Logo setup: $0.00 with your saved logo.';
    var fee = library ? library.fee : null;
    return fee ? 'One-time logo setup: ' + money(fee) + '.' : 'A one-time logo setup fee applies.';
  }

  return {
    url: url, parseEndpoint: parseEndpoint, parsePass: parsePass, withPass: withPass, nonce: nonce, requestId: requestId, whoamiBody: whoamiBody, libraryBody: libraryBody, previewBody: previewBody,
    parseItems: parseItems, familyAllowed: familyAllowed, buildLines: buildLines, base64: base64, uploadFile: uploadFile,
    prepareBody: prepareBody, parseAnswer: parseAnswer, readLibrary: readLibrary, checkoutUrl: checkoutUrl, parseHosts: parseHosts,
    subtotalCents: subtotalCents, moneyCents: moneyCents, previewSource: previewSource, setupLine: setupLine, money: money, GENERIC: GENERIC
  };
})();
if (typeof module !== 'undefined' && module.exports) module.exports = PyriteCompanyLogosCore;
